import { isPadala } from './service.js'

/**
 * One question on step 5, and the second number derived from it.
 *
 * The old form asked for both — *estimated total* and *max budget* — and left the
 * customer to work out why one shopping trip needed two amounts. It needs two because
 * the first is a guess and the second is the rider's authority to spend, and the gap
 * between them is what stops the rider ringing from the counter when the chicken turns
 * out to be ₱95 rather than the ₱80 lola remembered.
 *
 * Two boxes to explain that. One box and a rule explains it better.
 */

/** Shown in the copy as well as applied here, so the number is stated once. */
export const BUDGET_UPLIFT = 0.3

/** `estimatedTotal` and `maxBudget` are both 0–1,000,000 at the API. */
export const MONEY_CEILING = 1_000_000

// The same rounder as utils/fare.js and the server's Fare.pesos. Money rounds to
// centavos; 85.5 * 1.3 is 111.15000000000002 without it.
const pesos = (value) => Math.round(value * 100) / 100

/**
 * A form field, as a number — or `null` when nothing has been answered.
 *
 * `Number('')` is 0, which is why this cannot simply call it: an empty box is an
 * unanswered question, and showing a budget of ₱0 under one is an answer the customer
 * did not give.
 */
const amount = (value) => {
  if (value === null || value === undefined) return null

  // Trimmed first, because `Number('   ')` is also 0 — a box holding a stray space is
  // as unanswered as an empty one, and a number input can hold exactly that.
  const raw = typeof value === 'string' ? value.trim() : value

  if (raw === '') return null

  const parsed = Number(raw)

  if (!Number.isFinite(parsed) || parsed < 0) return null

  return parsed
}

/**
 * @param {string|number} estimatedTotal
 * @returns {number|null} — the suggested ceiling, or `null` if there is nothing to
 *   derive it from.
 */
export function maxBudgetFor(estimatedTotal) {
  const estimate = amount(estimatedTotal)

  if (estimate === null) return null

  /**
   * Clamped to the API's ceiling, and the clamp cannot break the rule it sits under.
   *
   * `maxBudget >= estimatedTotal` is enforced server-side. Capping the budget at a
   * million could in principle push it below an estimate — except the estimate is
   * capped at the same million, so the two meet rather than cross.
   */
  return Math.min(pesos(estimate * (1 + BUDGET_UPLIFT)), MONEY_CEILING)
}

/**
 * The two money fields as `POST /api/orders` wants them.
 *
 * @param {object} state — the form's own state
 * @returns {{estimatedTotal: number, maxBudget: number}}
 */
export function budgetFor(state) {
  /**
   * Padala buys nothing, so both are zero — and zero regardless of what is in the
   * fields.
   *
   * The customer can fill step 5 in as a pabili, go back, and switch. What they typed is
   * still in state, and sending it would hand a rider authority to spend money on an
   * order with nothing to buy. `0 >= 0` keeps the API's invariant intact.
   */
  if (isPadala(state)) {
    return { estimatedTotal: 0, maxBudget: 0 }
  }

  const estimatedTotal = amount(state?.estimatedTotal) ?? 0
  const edited = amount(state?.maxBudget)
  const suggested = maxBudgetFor(estimatedTotal) ?? 0

  return {
    estimatedTotal,
    // The suggestion is editable, so what the customer set wins — but never below the
    // estimate. Validation blocks that on the step; this is the second line, so a bug in
    // the form cannot reach the API as an opaque 400 the customer can do nothing about.
    maxBudget: Math.max(edited ?? suggested, estimatedTotal),
  }
}
